import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Broadcast, MapPin, ArrowRight, Trash, User as UserIcon, Clock, Warning as WarningIcon } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";

export default function AdminParkingLot() {
  const [rows, setRows] = useState(null);
  const [promoting, setPromoting] = useState(null); // job id

  const load = async () => {
    try {
      const r = await api.get("/parking-lot");
      setRows(r.data.jobs || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const promote = async (j) => {
    const title = window.prompt("Give this a proper title:", (j.title || "").replace("[Parking Lot] ", "") || "Work Order");
    if (title === null) return;
    setPromoting(j.id);
    try {
      const r = await api.post(`/jobs/${j.id}/promote`, { title: title.trim() });
      toast.success(`Promoted → ${r.data.title}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Promote failed");
    } finally {
      setPromoting(null);
    }
  };

  const drop = async (j) => {
    if (!window.confirm(`Drop this entry?\n\n${j.property_address}`)) return;
    try {
      await api.delete(`/jobs/${j.id}`);
      toast.success("Dropped");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <AdminShell>
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="closure-radar-page">
        <div className="flex items-center gap-3">
          <Broadcast weight="fill" size={30} className="text-amber-600" />
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
            ClosureRadar™
          </h1>
        </div>
        <p className="mt-1 text-sm text-slate-600 max-w-2xl">
          Jobs that still need something before they're fully closed — either quick-capture drops from the field, or bulk-closed jobs missing docs. Add photos or notes and close them out for real.
        </p>

        {rows === null && <div className="mt-8 text-center text-slate-500 font-bold uppercase text-xs">Loading…</div>}
        {rows?.length === 0 && (
          <div className="mt-6 rugged-card p-10 text-center" data-testid="closure-radar-empty">
            <Broadcast size={48} className="mx-auto text-slate-300" />
            <div className="mt-3 font-display font-black text-lg uppercase text-slate-500">ClosureRadar is clear</div>
            <div className="mt-1 text-sm text-slate-500">Everything is fully documented and closed. Nice.</div>
          </div>
        )}

        {rows?.length > 0 && (
          <div className="mt-6 space-y-2" data-testid="closure-radar-rows">
            {rows.map((j) => {
              const isNeedsInfo = j.status === "needs_info";
              return (
              <div key={j.id} data-testid={`closure-radar-row-${j.id}`}
                className="rugged-card p-4 flex flex-wrap items-center gap-3">
                {isNeedsInfo
                  ? <WarningIcon weight="fill" size={20} className="text-red-600 flex-shrink-0" />
                  : <MapPin weight="fill" size={20} className="text-amber-600 flex-shrink-0" />}
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-white ${isNeedsInfo ? "bg-red-600" : "bg-amber-500"}`}>
                      {isNeedsInfo ? "Needs Info" : "Quick Capture"}
                    </span>
                    {j.work_order_number && <span className="text-[10px] font-mono text-slate-500">WO# {j.work_order_number}</span>}
                  </div>
                  <div className="font-black text-sm mt-1">
                    {j.property_address}{j.unit ? ` · Unit ${j.unit}` : ""}
                  </div>
                  {(j.description || j.needs_info_reason) && (
                    <div className="text-xs italic text-slate-600 mt-0.5">
                      {j.needs_info_reason ? `Missing: ${j.needs_info_reason}` : `"${j.description}"`}
                    </div>
                  )}
                  <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    {j.assigned_to_name && <span className="inline-flex items-center gap-1"><UserIcon size={11}/> {j.assigned_to_name}</span>}
                    {j.created_at && <span className="inline-flex items-center gap-1"><Clock size={11}/> {new Date(j.created_at).toLocaleString()}</span>}
                  </div>
                </div>
                <Link to={`/admin/job/${j.id}`} data-testid={`closure-radar-open-${j.id}`}
                  className="h-9 px-3 border-2 border-[#0f172a] text-[11px] font-black uppercase tracking-widest hover:bg-slate-50">
                  Open
                </Link>
                {j.landing_pad && (
                  <button
                    onClick={() => promote(j)}
                    disabled={promoting === j.id}
                    data-testid={`closure-radar-promote-${j.id}`}
                    className="h-9 px-3 bg-emerald-600 text-white text-[11px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-1"
                  >
                    Promote <ArrowRight size={12} weight="bold" />
                  </button>
                )}
                <button
                  onClick={() => drop(j)}
                  data-testid={`closure-radar-drop-${j.id}`}
                  className="h-9 w-9 border-2 border-red-400 text-red-600 flex items-center justify-center hover:bg-red-50"
                  aria-label="Drop"
                >
                  <Trash size={14} weight="bold" />
                </button>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
